// @ts-strict-ignore
import * as React from 'react';
import { InfoCircleOutlined, MehOutlined, SmileOutlined } from '@ant-design/icons';
import Alert from 'antd/lib/alert/Alert';
import { Flex, Skeleton } from 'antd/lib';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import { useSelector } from 'react-redux';
import { OrgInsightsResponse } from '@/actions/entities';
import MarkdownParagraphWithEllipsis from '@/components/HOCs/MarkdownParagraphWithEllipsis';
import { colorWithAlpha, runwayColors } from '@/components/styles/globalStyles';
import { GetAppNameWithPlatform } from '@/components/orgOverview/orgOverviewHelpers';
import { useGetOrgBuildDistroBucketsQuery } from '@/middlewares/defaultApi';
import { RootState } from '@/reducers/reducers';
import { selectAllAppNamesAndPlatformsByAppId, selectIsDemoInstance, selectOrg } from '@/selectors/selectors';
import { selectDestinationsData } from '@/selectors/summaryViewSelectors';
import { AppPlatform, OrgOverviewAvailableMetric } from '@/types';
import { Components } from 'react-markdown';

interface OrgOverviewInsightsAlertProps {
    orgId: string;
    availableMetrics: OrgOverviewAvailableMetric[];
    insightsAlert?: OrgInsightsResponse;
    isLoading: boolean;
    onClose: () => void;
    onMetricScroll?: (metricId: string) => void;
}

export enum InsightsAlertType {
    Info = 'info',
    Warning = 'warning',
    Success = 'success',
}

enum InsightsAlertTokenType {
    App = 'app',
    Destination = 'destination',
    Metric = 'metric',
    Bucket = 'bucket',
    Org = 'org',
}

const insightsTokenRegex = new RegExp(`\\$\\{(${Object.values(InsightsAlertTokenType).join('|')}):([^}]+)\\}`, 'g');

export const OrgOverviewInsightsAlert: React.FC<OrgOverviewInsightsAlertProps> = ({
    orgId,
    availableMetrics,
    insightsAlert,
    isLoading,
    onClose,
    onMetricScroll,
}) => {
    const [hasProvidedFeedback, setHasProvidedFeedback] = React.useState(false);
    const isDemoInstance = useSelector((state: RootState) => selectIsDemoInstance(state, orgId));

    const scrollToMetric = React.useCallback((metricId: string) => {
        document.getElementById(metricId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    const markdownComponents = React.useMemo<Components>(
        () => ({
            a: ({ href, children, ...props }) => {
                if (href?.startsWith('#')) {
                    const metricId = href.slice(1);
                    return (
                        <span
                            {...props}
                            onClick={(event) => {
                                event.preventDefault();
                                scrollToMetric(metricId);
                                onMetricScroll?.(metricId);
                            }}
                            style={{
                                textDecorationLine: 'underline',
                                textDecorationStyle: 'dotted',
                                textUnderlineOffset: 3,
                                cursor: 'pointer',
                            }}
                        >
                            {children}
                        </span>
                    );
                }

                return (
                    <a {...props} href={href} target="_blank" rel="noreferrer">
                        {children}
                    </a>
                );
            },
        }),
        [onMetricScroll, scrollToMetric],
    );

    const org = useSelector((state: RootState) => selectOrg(state, orgId));
    const appNamesAndPlatformsByAppId = useSelector(selectAllAppNamesAndPlatformsByAppId);
    const orgDestinationsData = useSelector((state: RootState) => selectDestinationsData(state, orgId));
    const { data: orgBuildDistroBucketsResponse } = useGetOrgBuildDistroBucketsQuery({ orgId });

    const metricDisplayNameByType = React.useMemo(
        () =>
            availableMetrics.reduce(
                (acc, metric) => {
                    if (metric.displayName) {
                        acc[metric.type] = metric.displayName;
                    }

                    return acc;
                },
                {} as Record<string, string>,
            ),
        [availableMetrics],
    );

    const bucketDisplayNameById = React.useMemo(
        () =>
            (orgBuildDistroBucketsResponse?.data?.buckets ?? []).reduce(
                (acc, bucket) => {
                    acc[bucket.id] = bucket.name || bucket.id;
                    return acc;
                },
                {} as Record<string, string>,
            ),
        [orgBuildDistroBucketsResponse?.data?.buckets],
    );

    const insightsDestinationNamesById = React.useMemo(() => {
        return (orgDestinationsData?.appDestinations ?? []).reduce(
            (acc, { appName, destinations }) => {
                for (const destination of destinations) {
                    if (!destination.destinationId) {
                        continue;
                    }

                    const appInfo = destination.appId ? appNamesAndPlatformsByAppId[destination.appId] : null;
                    const platform = (destination.platform as AppPlatform) || appInfo?.platform;
                    const fallbackName =
                        appName && platform ?
                            GetAppNameWithPlatform(appName, platform, appInfo?.shouldDisplayAsMdm)
                        :   appName || destination.destinationId;

                    acc[destination.destinationId] = destination.name || fallbackName;
                    acc[destination.stepConfigId] = destination.name || fallbackName;
                }

                return acc;
            },
            {} as Record<string, string>,
        );
    }, [appNamesAndPlatformsByAppId, orgDestinationsData?.appDestinations]);

    const insightsAppNamesById = React.useMemo(
        () =>
            Object.entries(appNamesAndPlatformsByAppId).reduce(
                (acc, [appId, appInfo]) => {
                    acc[appId] =
                        appInfo?.name && appInfo?.platform ?
                            GetAppNameWithPlatform(appInfo.name, appInfo.platform, appInfo.shouldDisplayAsMdm)
                        :   appInfo?.name || appId;
                    return acc;
                },
                {} as Record<string, string>,
            ),
        [appNamesAndPlatformsByAppId],
    );

    const formatInsightsText = React.useCallback(
        (text?: string) => {
            if (!text) {
                return text;
            }

            return text.replace(insightsTokenRegex, (_match, tokenType: InsightsAlertTokenType, rawId: string) => {
                const id = rawId.trim();
                let resolvedText = id;

                switch (tokenType) {
                    case InsightsAlertTokenType.Destination:
                        resolvedText = insightsDestinationNamesById[id] || id;
                        break;
                    case InsightsAlertTokenType.App:
                        resolvedText = insightsAppNamesById[id] || id;
                        break;
                    case InsightsAlertTokenType.Metric:
                        resolvedText = metricDisplayNameByType[id] || id;
                        return `[${resolvedText}](#${id})`;
                    case InsightsAlertTokenType.Bucket:
                        resolvedText = bucketDisplayNameById[id] || id;
                        break;
                    case InsightsAlertTokenType.Org:
                        resolvedText = id === org?.id || id === org?.orgId ? org.name || id : id;
                        break;
                    default:
                        resolvedText = id;
                }

                return `**${resolvedText}**`;
            });
        },
        [bucketDisplayNameById, insightsAppNamesById, insightsDestinationNamesById, metricDisplayNameByType, org],
    );

    const getInsightsAlertColor = (): string => {
        switch (insightsAlert?.type) {
            case InsightsAlertType.Success:
                return runwayColors.statusGreen;
            case InsightsAlertType.Warning:
                return runwayColors.statusYellow;
            case InsightsAlertType.Info:
                return colorWithAlpha(runwayColors.primary, 0.25);
            default:
                return null;
        }
    };

    const getInsightsAlertIcon = (): React.ReactNode => {
        const iconStyle = { fontSize: 18, marginTop: 3, color: getInsightsAlertColor() };
        switch (insightsAlert?.type) {
            case InsightsAlertType.Success:
                return <SmileOutlined style={iconStyle} />;
            case InsightsAlertType.Warning:
                return <MehOutlined style={iconStyle} />;
            case InsightsAlertType.Info:
                return <InfoCircleOutlined style={iconStyle} />;
            default:
                return null;
        }
    };

    const handleFeedback = () => {
        setHasProvidedFeedback(true);
    };

    if (!isLoading && !insightsAlert) {
        return null;
    }

    return (
        <Alert
            type={insightsAlert?.type}
            closable
            onClose={onClose}
            style={{
                width: 800,
                marginTop: 20,
                padding: 19,
                border: isLoading ? 'none' : `1px solid ${getInsightsAlertColor()}`,
                borderRadius: 4,
                alignItems: 'flex-start',
            }}
            message={
                isLoading ?
                    <Skeleton
                        active
                        round={false}
                        style={{
                            padding: 12,
                        }}
                        paragraph={{
                            rows: 3,
                        }}
                    />
                :   <Space style={{ alignItems: 'flex-start' }} size={16}>
                        {getInsightsAlertIcon()}
                        <div>
                            <Flex
                                dir="row"
                                gap={6}
                                align="center"
                                style={{ fontSize: 16, marginBottom: 8, fontWeight: 'bold' }}
                            >
                                Recent insights
                            </Flex>
                            <MarkdownParagraphWithEllipsis
                                content={formatInsightsText(insightsAlert.description)}
                                maxHeight="250px"
                                style={{ fontSize: 14, marginBottom: 8 }}
                                backgroundColor="transparent"
                                markdownProps={{ components: markdownComponents }}
                            />
                            <Text type="secondary">Based on the past 90 days and all available metrics.</Text>
                            {!isDemoInstance && (
                                <Flex dir="row" gap={4} align="center">
                                    <Text type="secondary">
                                        AI insights are in beta.{' '}
                                        {hasProvidedFeedback ? 'Thank you!' : 'Was this helpful?'}
                                    </Text>
                                    {!hasProvidedFeedback && (
                                        <Flex dir="row" gap={4} align="center" style={{ marginLeft: 2 }}>
                                            {['Yes', 'No', 'Send a message'].map((label, index) => (
                                                <div key={label}>
                                                    <Text
                                                        type="secondary"
                                                        onClick={handleFeedback}
                                                        style={{
                                                            textDecorationLine: 'underline',
                                                            textDecorationStyle: 'dotted',
                                                            textUnderlineOffset: 3,
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        {label}
                                                    </Text>
                                                    {index < 2 && (
                                                        <Text type="secondary" style={{ marginLeft: 4 }}>
                                                            /
                                                        </Text>
                                                    )}
                                                </div>
                                            ))}
                                        </Flex>
                                    )}
                                </Flex>
                            )}
                        </div>
                    </Space>
            }
        />
    );
};
